"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Calendar, ChevronDown, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MasteringTheBrush() {
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const {
    ref: actionsRef,
    isVisible: actionsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: trustRef,
    isVisible: trustVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageRefVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const trustMetrics = [{
    value: "12+ Years",
    label: "Mastery in Craft"
  }, {
    value: "2,500+",
    label: "Homes Transformed"
  }, {
    value: "5.0",
    label: "Quality Rating"
  }];
  return <section data-section-id="609" id="mastering-the-brush" className="relative overflow-hidden bg-background text-foreground">
      {}
      <svg aria-hidden="true" className="pointer-events-none absolute top-8 right-8 hidden h-40 w-40 opacity-20 md:block lg:h-56 lg:w-56" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1" style={{
      color: "var(--primary)"
    }}>
        <path d="M30 170 Q 80 120, 120 70 T 180 20" strokeLinecap="round" />
        <ellipse cx="60" cy="135" rx="14" ry="6" transform="rotate(-30 60 135)" />
        <ellipse cx="85" cy="110" rx="14" ry="6" transform="rotate(-35 85 110)" />
        <ellipse cx="110" cy="85" rx="14" ry="6" transform="rotate(-40 110 85)" />
        <ellipse cx="135" cy="60" rx="14" ry="6" transform="rotate(-45 135 60)" />
        <ellipse cx="160" cy="40" rx="12" ry="5" transform="rotate(-50 160 40)" />
        <ellipse cx="75" cy="148" rx="12" ry="5" transform="rotate(30 75 148)" />
        <ellipse cx="100" cy="123" rx="12" ry="5" transform="rotate(25 100 123)" />
        <ellipse cx="125" cy="98" rx="12" ry="5" transform="rotate(20 125 98)" />
      </svg>

      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-6 pt-16 pb-20 sm:px-10 lg:grid-cols-[60fr_40fr] lg:gap-16 lg:px-16 lg:pt-24 lg:pb-28 xl:px-24 xl:pl-24">
        {}
        <div className="flex flex-col justify-center lg:pr-8">
          {}
          <div ref={labelRef} className={`flex items-center gap-3 transition-all duration-700 ease-out ${labelVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <span className="h-px w-8" style={{
            backgroundColor: "var(--primary)"
          }} />
            <Badge variant="outline" className="border-0 bg-transparent px-0 py-0 text-xs font-semibold tracking-[0.2em] uppercase font-mono" style={{
            color: "var(--primary)"
          }}>
              Elite Brushstrokes Painting
            </Badge>
          </div>

          {}
          <h1 ref={headlineRef} className={`mt-6 font-serif leading-[1.05] tracking-tight text-foreground transition-all delay-100 duration-700 ease-out ${headlineVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`} style={{
          fontSize: "clamp(2.75rem, 6vw, 5.5rem)"
        }}>
            Precision painting for{" "}
            <em className="font-serif font-normal italic" style={{
            color: "var(--primary)"
          }}>
              refined
            </em>{" "}
            living spaces.
          </h1>

          {}
          <p ref={bodyRef} className={`mt-8 max-w-[480px] text-lg leading-relaxed font-default text-muted-foreground transition-all delay-200 duration-700 ease-out ${bodyVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            From meticulous interior finishes to durable exterior transformations, our team 
            brings architectural precision to every stroke. We don't just paint; we elevate 
            the character of your home with professional care and premium pigments.
          </p>

          {}
          <div ref={actionsRef} className={`mt-10 flex flex-col items-start gap-4 transition-all delay-300 duration-700 ease-out sm:flex-row sm:items-center sm:gap-6 ${actionsVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <Link to="/GetAQuotePage">
              <Button size="lg" className="h-12 gap-2 rounded-lg bg-primary px-7 text-base font-medium text-primary-foreground shadow-lg hover:bg-primary/90">
                <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                Request a Free Quote
              </Button>
            </Link>
            <Link to="/OurCraftPage" className="group inline-flex items-center gap-1 text-base font-medium text-foreground">
              <span className="underline-offset-[6px] transition-all group-hover:underline" style={{
              textDecorationColor: "var(--primary)"
            }}>
                Explore our craft
              </span>
              <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">
                →
              </span>
            </Link>
          </div>

          {}
          <div ref={trustRef} className={`mt-12 transition-all delay-500 duration-700 ease-out ${trustVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <div className="h-px w-full" style={{
            backgroundColor: "var(--border)"
          }} />
            <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-3 sm:gap-x-4">
              {trustMetrics.map((metric, i) => <div data-index={i} key={metric.value} className="flex items-center gap-2 sm:gap-4">
                  {i > 0 && <span aria-hidden="true" className="inline-block size-1.5 rounded-lg" style={{
                backgroundColor: "var(--primary)"
              }} />}
                  <div className="flex flex-col">
                    <span className="font-serif text-lg font-semibold text-foreground">
                      {metric.value}
                    </span>
                    <span className="text-xs tracking-wide text-muted-foreground uppercase font-mono">
                      {metric.label}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {}
        <div className="relative flex items-center justify-center lg:justify-end">
          <div ref={imageRef} className="relative w-full max-w-md lg:max-w-none">
            {}
            <div className="relative overflow-hidden shadow-2xl" style={{
            borderRadius: "var(--radius)",
            clipPath: imageRefVisible ? "inset(0 0 0 0)" : "inset(100% 0 0 0)",
            transition: "clip-path 900ms cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/painter_at_work.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/painter_at_work.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/painter_at_work.png-wvc-sizes" alt="Professional painter applying finish to a wall" loading="lazy" decoding="async" className="aspect-[3/4] w-full object-cover" />
            </div>

            {}
            <div className={`absolute -bottom-6 -left-4 flex items-center gap-3 rounded-lg bg-card px-5 py-4 shadow-lg transition-all delay-700 duration-700 ease-out sm:-left-8 ${imageRefVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
              <span className="relative flex size-3 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-lg opacity-75" style={{
                backgroundColor: "var(--primary)"
              }} />
                <span className="relative inline-flex size-2.5 rounded-lg" style={{
                backgroundColor: "var(--primary)"
              }} />
              </span>
              <div className="flex flex-col">
                <span className="text-[10px] tracking-[0.15em] text-muted-foreground uppercase font-mono">
                  Current Project
                </span>
                <span className="font-serif text-base font-semibold text-card-foreground">
                  Residential Renovation
                </span>
              </div>
            </div>

            {}
            <div className={`absolute -top-4 -right-3 flex size-24 flex-col items-center justify-center rounded-lg p-3 text-center shadow-lg transition-all delay-700 duration-700 ease-out sm:size-28 sm:-right-6 ${imageRefVisible ? "scale-100 opacity-100" : "scale-90 opacity-0"}`} style={{
            backgroundColor: "var(--primary)",
            color: "var(--primary-foreground)",
            borderRadius: "var(--radius)"
          }}>
              <Phone className="mb-1 size-4" />
              <span className="font-serif text-sm leading-tight font-semibold">24/7</span>
              <span className="text-[10px] leading-tight">Consultation</span>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="flex justify-center pb-8">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <span className="text-[10px] tracking-[0.25em] uppercase font-mono">Scroll</span>
          <ChevronDown className="size-4 animate-bounce" aria-hidden="true" />
        </div>
      </div>
    </section>;
}